import React, { useState } from 'react';
import { getApiErrorMessage } from '../lib/apiError';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import apiClient from '../lib/axios';
import { AuthLayout } from '../components/AuthLayout';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiClient.post('/auth/forgot-password', { email: email.toLowerCase() });
      setSuccess(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to send reset email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Forgot Password" subtitle="Enter your email and we'll send you a reset link.">
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          If an account exists with that email, a password reset link has been sent.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          disabled={loading || success}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !email || success}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
      </form>

      <Button variant="text" fullWidth onClick={() => navigate('/login')}>
        Back to Login
      </Button>
    </AuthLayout>
  );
};
